/**
 * ADS-B API
 * Handles SSE connection to the tactical warning system for aircraft alerts
 */

/**
 * Connect to the ADS-B alerts SSE endpoint for a specific cell
 * @param cellId - The cell ID to stream alerts for
 * @param signal - Optional signal used to cancel an in-flight connection
 * @returns Promise resolving to an EventSource connection
 */
export const connectAdsbAlertsSse = (
  cellId: string,
  signal?: AbortSignal
): Promise<EventSource> => {
  const baseUrl = process.env.REACT_APP_TWS_ALERTS_SSE_URL;

  if (!baseUrl) {
    console.error('[ADSB API] REACT_APP_TWS_ALERTS_SSE_URL is not configured.');
    return Promise.reject(new Error('REACT_APP_TWS_ALERTS_SSE_URL is not configured'));
  }

  const eventSourceUrl = `${baseUrl}/stream-alerts/cell/${cellId}`;

  return new Promise((resolve, reject) => {
    const connection = new EventSource(eventSourceUrl);
    let hasConnected = false;
    let cancelled = false;

    const handleAbort = () => {
      cancelled = true;
      connection.close();
      reject(new Error('ADS-B SSE connection cancelled'));
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }
    signal?.addEventListener('abort', handleAbort, { once: true });

    connection.onopen = () => {
      if (cancelled) {
        connection.close();
        return;
      }
      signal?.removeEventListener('abort', handleAbort);
      console.log('[ADSB API] SSE connection opened successfully');
      hasConnected = true;
      resolve(connection);
    };

    connection.onerror = () => {
      // Only reject if we haven't connected yet (initial connection failure)
      if (!hasConnected && !cancelled) {
        signal?.removeEventListener('abort', handleAbort);
        console.error('[ADSB API] SSE initial connection failed');
        connection.close();
        reject(new Error('Failed to establish ADS-B SSE connection'));
      }
      // After initial connection, errors are handled by the caller
    };
  });
};
